import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Shield, CheckCircle2, AlertTriangle, Loader2, Plug, Key, Eye, EyeOff, Send, Mail } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('account');
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchSub = async () => {
      try {
        const res = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
        setSubscription(res.data);
      } catch (e) {
        if (e.response?.status === 401) navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchSub();
  }, [navigate]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure? Your plan will remain active until the end of your billing period.')) return;
    setCanceling(true);
    try {
      await axios.post(`${API}/billing/cancel`, {}, { withCredentials: true });
      const res = await axios.get(`${API}/billing/subscription`, { withCredentials: true });
      setSubscription(res.data);
    } catch (e) { /* silent */ }
    setCanceling(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F4EF' }}>
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
        </div>
      </AppShell>
    );
  }

  const tier = subscription?.tier || 'free';
  const isFree = tier === 'free';
  const isCanceling = subscription?.cancel_at_period_end;

  const tierDisplay = {
    free: { label: 'Free', color: '#5C5347', bg: '#F7F4EF' },
    starter: { label: 'Starter', color: '#1D6B4F', bg: 'rgba(29,107,79,0.1)' },
    professional: { label: 'Professional', color: '#C67A5C', bg: 'rgba(198,122,92,0.1)' },
  }[tier] || { label: 'Free', color: '#5C5347', bg: '#F7F4EF' };

  const TABS = [
    { id: 'account', label: 'Account' },
    ...(isAdmin ? [{ id: 'integrations', label: 'Integrations' }] : []),
    { id: 'digest', label: 'Email Digest' },
  ];

  return (
    <AppShell active="settings">
      <div className="min-h-screen pb-24 desktop:pb-8" style={{ backgroundColor: '#F7F4EF' }} data-testid="settings-page">
        <header className="px-6 pt-6 pb-2">
          <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm hover:opacity-70 transition-opacity mb-4" style={{ color: '#5C5347' }} data-testid="settings-back-btn">
            <ArrowLeft size={16} /> Back
          </button>
          <h1 className="text-2xl font-light" style={{ color: '#3C3226' }}>Settings</h1>
        </header>

        {/* Tab bar */}
        <div className="px-6 max-w-lg mx-auto flex items-center gap-1 mb-6 pb-px" style={{ borderBottom: '1px solid #DDD5C8' }}>
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-4 py-2.5 text-[11px] uppercase tracking-wider transition-colors"
              style={{
                color: activeTab === tab.id ? '#3C3226' : '#8C7E6E',
                fontWeight: activeTab === tab.id ? 600 : 400,
                borderBottom: activeTab === tab.id ? '2px solid #B8985A' : '2px solid transparent',
                marginBottom: '-1px',
              }}
              data-testid={`settings-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <main className="px-6 max-w-lg mx-auto space-y-4">
          {activeTab === 'account' && (
            <>
              {/* Account */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
                <h3 className="text-sm font-medium mb-3" style={{ color: '#3C3226' }}>Account</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#5C5347' }}>Email</span>
                    <span style={{ color: '#3C3226' }}>{user?.email || '--'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#5C5347' }}>Name</span>
                    <span style={{ color: '#3C3226' }}>{user?.name || '--'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#5C5347' }}>Role</span>
                    <span className="capitalize" style={{ color: '#3C3226' }}>{user?.role || 'user'}</span>
                  </div>
                </div>
              </motion.div>

              {/* Subscription */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium" style={{ color: '#3C3226' }}>Subscription</h3>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1" style={{ color: tierDisplay.color, backgroundColor: tierDisplay.bg }} data-testid="current-tier-badge">
                    {tierDisplay.label}
                  </span>
                </div>
                <div className="space-y-3">
                  {!isFree && (
                    <>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 size={15} style={{ color: '#1D6B4F' }} />
                        <span style={{ color: '#3C3226' }}>{tier === 'starter' ? '$29' : '$49'}/month</span>
                      </div>
                      {subscription?.current_period_end && (
                        <div className="flex items-center gap-2 text-sm">
                          <CreditCard size={15} style={{ color: '#5C5347' }} />
                          <span style={{ color: '#5C5347' }}>
                            {isCanceling ? 'Cancels' : 'Renews'}: {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </span>
                        </div>
                      )}
                      {isCanceling && (
                        <div className="flex items-center gap-2 p-3" style={{ backgroundColor: 'rgba(239,159,39,0.1)' }}>
                          <AlertTriangle size={15} style={{ color: '#EF9F27' }} />
                          <span className="text-xs" style={{ color: '#EF9F27' }}>Plan set to cancel at billing period end.</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex gap-2 pt-1">
                    {isFree ? (
                      <button onClick={() => navigate('/pricing')} className="flex-1 py-2.5 text-sm font-medium transition-opacity hover:opacity-90" style={{ backgroundColor: '#C67A5C', color: '#F0EBE3' }} data-testid="upgrade-from-settings">
                        Upgrade your plan
                      </button>
                    ) : (
                      <>
                        <button onClick={() => navigate('/pricing')} className="flex-1 py-2.5 text-sm font-medium transition-opacity hover:opacity-80" style={{ backgroundColor: '#F7F4EF', color: '#3C3226', border: '1px solid #D4C9B8' }} data-testid="change-plan-btn">
                          Change plan
                        </button>
                        {!isCanceling && (
                          <button onClick={handleCancel} disabled={canceling} className="py-2.5 px-4 text-sm transition-opacity hover:opacity-80 disabled:opacity-50" style={{ border: '1px solid #D4C9B8', color: '#5C5347' }} data-testid="cancel-plan-btn">
                            {canceling ? <Loader2 size={14} className="animate-spin" /> : 'Cancel'}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Features */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
                <h3 className="text-sm font-medium mb-3" style={{ color: '#3C3226' }}>Your Features</h3>
                <div className="space-y-2">
                  {(subscription?.features || []).map(f => (
                    <div key={f} className="flex items-center gap-2 text-sm">
                      <Shield size={14} style={{ color: '#1D6B4F' }} strokeWidth={1.5} />
                      <span className="capitalize" style={{ color: '#3C3226' }}>{f.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Sign Out */}
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <button onClick={handleLogout} className="w-full py-3 text-sm font-medium transition-opacity hover:opacity-80" style={{ border: '1px solid #D4C9B8', color: '#5C5347' }} data-testid="settings-logout-btn">
                  Sign out
                </button>
              </motion.div>
            </>
          )}

          {activeTab === 'integrations' && isAdmin && <IntegrationsTab />}
          {activeTab === 'digest' && <DigestTab />}
        </main>
      </div>
    </AppShell>
  );
}


/* ══════════════════════════════════════════
   INTEGRATIONS TAB (Admin only)
   ══════════════════════════════════════════ */
function IntegrationsTab() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [showValues, setShowValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/integrations`, { headers: authHeaders() })
      .then(r => setIntegrations(r.data.integrations || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    const keys = {};
    for (const [keyId, value] of Object.entries(editing)) {
      if (value !== undefined && value !== '') {
        keys[keyId] = value;
      }
    }
    if (Object.keys(keys).length === 0) {
      setSaveMsg('No changes to save.');
      setSaving(false);
      return;
    }
    try {
      const res = await axios.put(`${API}/admin/integrations`, { keys }, { headers: authHeaders() });
      setSaveMsg(`Saved! ${res.data.configured_count} keys configured.`);
      setEditing({});
      // Refresh
      const r = await axios.get(`${API}/admin/integrations`, { headers: authHeaders() });
      setIntegrations(r.data.integrations || []);
    } catch (e) {
      setSaveMsg('Failed to save. Check your permissions.');
    }
    setSaving(false);
    setTimeout(() => setSaveMsg(''), 3000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const groups = [
    { title: 'Payments', keys: ['stripe'] },
    { title: 'Email & SMS', keys: ['sendgrid', 'twilio_sid', 'twilio_token', 'twilio_phone'] },
    { title: 'AI Engines', keys: ['perplexity', 'serpapi', 'xai'] },
    { title: 'Other', keys: ['google_places'] },
  ];

  return (
    <div data-testid="integrations-tab">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Plug size={16} style={{ color: '#B8985A' }} />
          <h2 className="text-sm font-medium" style={{ color: '#3C3226' }}>API Integrations</h2>
        </div>
        <p className="text-[12px]" style={{ color: '#8C7E6E' }}>
          Add your API keys to enable production features. Keys are stored securely and never exposed in the frontend.
        </p>
      </motion.div>

      {groups.map(group => {
        const groupIntegrations = integrations.filter(i => group.keys.includes(i.key_id));
        if (groupIntegrations.length === 0) return null;
        return (
          <motion.div key={group.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 mb-3" style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}>
            <p className="text-[10px] uppercase tracking-wider font-medium mb-3" style={{ color: '#8C7E6E' }}>{group.title}</p>
            <div className="space-y-3">
              {groupIntegrations.map(integration => (
                <div key={integration.key_id}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: integration.configured ? '#1D6B4F' : '#D4C9B8' }} />
                      <span className="text-[12px] font-medium" style={{ color: '#3C3226' }}>{integration.label}</span>
                    </div>
                    <span className="text-[10px]" style={{ color: integration.configured ? '#1D6B4F' : '#8C7E6E' }}>
                      {integration.configured ? 'Connected' : 'Not configured'}
                    </span>
                  </div>
                  <p className="text-[10px] mb-1.5" style={{ color: '#8C7E6E' }}>{integration.description}</p>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <input
                        type={showValues[integration.key_id] ? "text" : "password"}
                        value={editing[integration.key_id] !== undefined ? editing[integration.key_id] : ''}
                        onChange={e => setEditing(prev => ({ ...prev, [integration.key_id]: e.target.value }))}
                        placeholder={integration.configured ? integration.masked_value : integration.placeholder}
                        className="w-full px-3 py-2 text-[11px] outline-none pr-8"
                        style={{ backgroundColor: '#F7F4EF', border: '1px solid #E8E2D8', color: '#3C3226' }}
                        data-testid={`integration-input-${integration.key_id}`}
                      />
                      <button
                        onClick={() => setShowValues(prev => ({ ...prev, [integration.key_id]: !prev[integration.key_id] }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2"
                        style={{ color: '#8C7E6E' }}
                      >
                        {showValues[integration.key_id] ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        );
      })}

      {/* Save Button */}
      <div className="flex items-center gap-3 mt-4">
        <button onClick={handleSave} disabled={saving || Object.keys(editing).length === 0} className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="save-integrations-btn">
          {saving ? 'Saving...' : 'Save Keys'}
        </button>
        {saveMsg && <span className="text-[11px]" style={{ color: saveMsg.includes('Failed') ? '#C67A5C' : '#1D6B4F' }}>{saveMsg}</span>}
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════
   DIGEST TAB (Email preferences)
   ══════════════════════════════════════════ */
function DigestTab() {
  const [emailStatus, setEmailStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/digest/email-status`, { headers: authHeaders() }),
      axios.get(`${API}/digest/settings`, { withCredentials: true }),
    ]).then(([statusRes, settingsRes]) => {
      setEmailStatus(statusRes.data);
      setSettings(settingsRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const toggleSetting = async (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    try {
      await axios.put(`${API}/digest/settings`, updated, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const sendTest = async () => {
    setSending(true);
    setSendResult('');
    try {
      const res = await axios.post(`${API}/digest/send-test`, {}, { headers: authHeaders() });
      setSendResult(res.data.message || res.data.status);
    } catch (e) {
      setSendResult('Failed to send test email.');
    }
    setSending(false);
    setTimeout(() => setSendResult(''), 5000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D4C9B8', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div data-testid="digest-tab">
      {/* Email Status */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-4 mb-4" style={{ backgroundColor: emailStatus?.configured ? 'rgba(29,107,79,0.05)' : '#FDFBF7', border: `1px solid ${emailStatus?.configured ? 'rgba(29,107,79,0.2)' : '#DDD5C8'}` }}>
        <div className="flex items-center gap-2 mb-1">
          <Mail size={14} style={{ color: emailStatus?.configured ? '#1D6B4F' : '#8C7E6E' }} />
          <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: emailStatus?.configured ? '#1D6B4F' : '#8C7E6E' }}>
            {emailStatus?.configured ? 'Email Delivery Active' : 'Email Not Configured'}
          </span>
        </div>
        <p className="text-[11px]" style={{ color: '#5C5347' }}>{emailStatus?.message}</p>
      </motion.div>

      {/* Digest Preferences */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="p-5" style={{ backgroundColor: '#F7F4EF', border: '1px solid #D4C9B8' }}>
        <h3 className="text-sm font-medium mb-4" style={{ color: '#3C3226' }}>Monday Morning Digest</h3>
        <p className="text-[12px] mb-4" style={{ color: '#8C7E6E' }}>
          Receive a weekly email summary every Monday with your score changes, alerts, and recommended actions.
        </p>

        {settings && (
          <div className="space-y-3">
            {[
              { key: 'enabled', label: 'Enable weekly digest' },
              { key: 'include_score', label: 'Include score changes' },
              { key: 'include_alerts', label: 'Include alerts' },
              { key: 'include_competitors', label: 'Include competitor updates' },
              { key: 'include_actions', label: 'Include recommended actions' },
            ].map(item => (
              <button key={item.key} onClick={() => toggleSetting(item.key)} className="w-full flex items-center justify-between py-2" data-testid={`digest-toggle-${item.key}`}>
                <span className="text-[12px]" style={{ color: '#3C3226' }}>{item.label}</span>
                <div className="w-9 h-5 rounded-full relative transition-colors" style={{ backgroundColor: settings[item.key] ? '#1D6B4F' : '#D4C9B8' }}>
                  <div className="absolute w-3.5 h-3.5 rounded-full bg-white top-[3px] transition-all" style={{ left: settings[item.key] ? '18px' : '3px' }} />
                </div>
              </button>
            ))}
          </div>
        )}
      </motion.div>

      {/* Send Test */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4">
        <div className="flex items-center gap-3">
          <button onClick={sendTest} disabled={sending} className="px-4 py-2.5 text-[11px] font-medium uppercase tracking-wider flex items-center gap-1.5 transition-opacity hover:opacity-90 disabled:opacity-40" style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }} data-testid="send-test-digest-btn">
            <Send size={11} /> {sending ? 'Sending...' : 'Send Test Digest'}
          </button>
          {sendResult && <span className="text-[11px]" style={{ color: sendResult.includes('not_configured') || sendResult.includes('Failed') ? '#C67A5C' : '#1D6B4F' }}>{sendResult}</span>}
        </div>
        <p className="text-[10px] mt-2" style={{ color: '#8C7E6E' }}>
          {emailStatus?.configured ? 'Sends a preview digest to your email address.' : 'Configure SendGrid in Integrations first.'}
        </p>
      </motion.div>

      {/* Preview Link */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-4">
        <a href="/digest-preview" className="text-[11px] uppercase tracking-wider underline underline-offset-4" style={{ color: '#B8985A' }} data-testid="digest-preview-link">
          Preview digest content
        </a>
      </motion.div>
    </div>
  );
}
